'use strict';

(() => {
  const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

  function safelyDecode(value) {
    if (!value) return '';

    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  function getYouTubeId(value) {
    if (!value) return null;

    const input = safelyDecode(String(value).trim());

    try {
      const url = new URL(input, window.location.origin);
      const hostname = url.hostname.replace(/^www\./, '').replace(/^m\./, '');

      let videoId = null;

      // https://youtu.be/VIDEO_ID
      if (hostname === 'youtu.be') {
        videoId = url.pathname.split('/').filter(Boolean)[0];
      }

      // youtube.com URLs
      if (hostname === 'youtube.com' || hostname === 'youtube-nocookie.com') {
        // https://youtube.com/watch?v=VIDEO_ID
        if (url.searchParams.get('v')) {
          videoId = url.searchParams.get('v');
        }

        const pathParts = url.pathname.split('/').filter(Boolean);

        // Supports:
        // /embed/VIDEO_ID
        // /shorts/VIDEO_ID
        // /live/VIDEO_ID
        // /v/VIDEO_ID
        if (!videoId && pathParts.length >= 2 && ['embed', 'shorts', 'live', 'v'].includes(pathParts[0])) {
          videoId = pathParts[1];
        }
      }

      return videoId && YOUTUBE_ID_PATTERN.test(videoId) ? videoId : null;
    } catch {
      return null;
    }
  }

  function getVimeoId(value) {
    if (!value) return null;

    const input = safelyDecode(String(value).trim());

    try {
      const url = new URL(input, window.location.origin);
      const hostname = url.hostname.replace(/^www\./, '');

      if (hostname !== 'vimeo.com' && hostname !== 'player.vimeo.com') {
        return null;
      }

      const pathParts = url.pathname.split('/').filter(Boolean);

      // Vimeo IDs are numeric. Searching backwards supports URLs like:
      // vimeo.com/123456789
      // player.vimeo.com/video/123456789
      // vimeo.com/channels/channel-name/123456789
      const videoId = [...pathParts].reverse().find((part) => /^\d+$/.test(part));

      return videoId || null;
    } catch {
      return null;
    }
  }

  function getEmbedUrl(value) {
    const youtubeId = getYouTubeId(value);

    if (youtubeId) {
      return `https://www.youtube.com/embed/${youtubeId}?rel=0&vq=720`;
    }

    const vimeoId = getVimeoId(value);

    if (vimeoId) {
      return `https://player.vimeo.com/video/${vimeoId}?byline=0&portrait=0&badge=0`;
    }

    return null;
  }

  class EmbedVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      let settings;

      try {
        settings = JSON.parse(this.dataset.settingsJson || '{"videos":[]}');
      } catch (error) {
        console.error('[EmbedVideo] Could not parse video settings:', error);

        return;
      }

      const videos = Array.isArray(settings.videos) ? settings.videos : [];

      videos.forEach((video) => {
        const embedUrl = getEmbedUrl(video);

        if (!embedUrl) {
          console.warn('[EmbedVideo] Unsupported or invalid video URL:', video);

          return;
        }

        const wrapper = document.createElement('div');
        wrapper.className = 'video-wrapper';

        const iframe = document.createElement('iframe');

        iframe.src = embedUrl;
        iframe.width = '850';
        iframe.height = '480';
        iframe.title = 'Video';
        iframe.loading = 'lazy';
        iframe.allowFullscreen = true;
        iframe.setAttribute(
          'allow',
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
        );
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

        wrapper.appendChild(iframe);
        this.appendChild(wrapper);
      });
    }
  }

  if (!customElements.get('embed-video')) {
    customElements.define('embed-video', EmbedVideo);
  }
})();
